// 按 robot_id 分片的实时状态（由实时总线写入）。组件按 id 选择，避免一台更新全表重渲染。

import type { LeaseInfo, ServerMessage, Severity, Telemetry } from "@primebot/proto-ts";
import { create } from "zustand";
import type { ProximityEntry } from "@/lib/proximity";

export type LiveEvent = { kind: string; severity: Severity; ts: number; payload: unknown };

export type LiveRobot = {
  online?: boolean;
  telemetry?: Telemetry;
  /** 本地收到遥测的时间（ms） */
  telemetryAt?: number;
  /** undefined = 还不知道；null = 没人控制 */
  holder?: LeaseInfo | null;
  denied?: boolean;
  /** 最近的实时事件（新的在前，最多 50 条） */
  events?: LiveEvent[];
};

type State = { robots: Record<string, LiveRobot> };

export const useLive = create<State>(() => ({ robots: {} }));

const MAX_EVENTS = 50;

/**
 * 障碍感知（5 Hz）单独一个 store：放进 `useLive` 会让订阅整台机器人状态的页面（遥控台）每秒重渲染 5 次。
 * 只存最新一帧与本地收到时间；过期由显示侧按时间判断（开发计划 14 §2）。
 */
export const useProximityStore = create<{ byRobot: Record<string, ProximityEntry> }>(() => ({ byRobot: {} }));

function dropProximity() {
  useProximityStore.setState({ byRobot: {} });
}

export const useProximity = (id: string | undefined): ProximityEntry | undefined =>
  useProximityStore((s) => (id ? s.byRobot[id] : undefined));

function patch(id: string, f: (r: LiveRobot) => LiveRobot) {
  useLive.setState((s) => ({ robots: { ...s.robots, [id]: f(s.robots[id] ?? {}) } }));
}

export function applyServerMessage(m: ServerMessage, now = Date.now()) {
  switch (m.t) {
    case "telemetry":
      patch(m.p.robot_id, (r) => ({ ...r, telemetry: m.p.telemetry, telemetryAt: now, online: true }));
      break;
    case "robot_online":
      // 下线不清障碍帧：留着让显示侧按本地收到时间判"链路过期"（直接清掉会变成"还没收到"、前方提示消失）
      patch(m.p.robot_id, (r) => ({ ...r, online: m.p.online }));
      break;
    case "proximity":
      useProximityStore.setState((s) => ({ byRobot: { ...s.byRobot, [m.p.robot_id]: { p: m.p.proximity, at: now } } }));
      break;
    case "lease_changed":
      patch(m.p.robot_id, (r) => ({ ...r, holder: m.p.holder }));
      break;
    case "lease_revoked":
      patch(m.p.robot_id, (r) => (r.holder?.lease_id === m.p.lease_id ? { ...r, holder: null } : r));
      break;
    case "event": {
      const id = m.p.robot_id;
      if (!id) break;
      const ev: LiveEvent = { kind: m.p.kind, severity: m.p.severity, ts: m.p.ts, payload: m.p.payload };
      patch(id, (r) => ({ ...r, events: [ev, ...(r.events ?? [])].slice(0, MAX_EVENTS) }));
      break;
    }
    case "subscribe_result":
      useLive.setState((s) => {
        const robots = { ...s.robots };
        for (const id of m.p.accepted) if (robots[id]?.denied) robots[id] = { ...robots[id], denied: false };
        for (const id of m.p.denied) robots[id] = { ...(robots[id] ?? {}), denied: true };
        return { robots };
      });
      break;
    default:
      break;
  }
}

export function resetLive() {
  useLive.setState({ robots: {} });
  dropProximity();
}

/** 实时连接断开：在线/遥测/持有者都不再可信，清掉（页面回退到 REST 数据），重连后由初始推送补齐。 */
export function staleLive() {
  useLive.setState((s) => {
    const robots: Record<string, LiveRobot> = {};
    for (const [id, r] of Object.entries(s.robots)) robots[id] = { events: r.events };
    return { robots };
  });
  // 障碍帧保留：显示侧 1 s 内判成"链路过期"
}

export const useLiveRobot = (id: string | undefined): LiveRobot | undefined =>
  useLive((s) => (id ? s.robots[id] : undefined));
